/**
 * ประลองมือ (data/duelMatch.ts, docs/research/C0_PVP.md): a best-of-three bout with the rival who waits at the hub.
 *
 * - The rival (an NPC at the hub) is challenged with the ordinary `duelChallenge` command; this system claims it for
 *   its own rival (`refuse`), every other target stays with systems/duelGround.ts.
 * - A taken-up bout puts a second player into the World (`World.addPlayer`) with a copy of the challenger's build, and
 *   it fights on the World's `pvpBout` rule (the two may hurt only each other; nobody dies: a deadly blow leaves 1 HP),
 *   exactly as a duel between two real players does (systems/pvpDuel.ts). The rival's mind (systems/duelBot.ts) sends
 *   that player's commands through `World.apply(cmd, id)`, so it is a stand-in for a person and nothing else differs.
 * - Rounds: 3-2-1, fight to a tenth of the health (or the round clock: more health left wins), both whole again for the
 *   next, first to two rounds. Blows shove the one struck a little (feel). Win or lose, nothing is lost.
 * - After the bout: `duelMatch rematch` (the rival learns: a win makes it sharper, a loss gentler) or `leave`.
 * The bout reports itself with the same `pvpDuel` events a server-arranged duel sends, so one HUD serves both.
 */
import { holdResultPose, resultPose } from './duelResultPose';
import {
  bigMove,
  DUEL_BUILDS,
  DUEL_COUNTERS,
  DUEL_FLAGS,
  DUEL_GUARD,
  DUEL_TRI,
  DUEL_RAGE,
  DUEL_LOCK,
  DUEL_MATCH,
  DUEL_MOVE,
  DUEL_NAMES,
  DUEL_STYLES,
  DUEL_V2,
  DUEL_FORMAT,
  type DuelStyle,
} from '../data/duelMatch';
import { ARENA_ROSTER } from '../data/arena';
import { FAIR_ARENA } from '../data/tournament';
import { moveLocked } from '../data/moveUnlocks';
import { RAGE, getArt, getSkill, type SkillKind } from '../data/skills';
import { stacksOf } from '../status';
import { familyOf, isArmed } from '../data/weaponFamilies';
import { getNeigong } from '../data/neigong';
import { RING } from '../data/layouts/plains';
import { plains256 } from '../data/zones';
import { freeAt, nearestFloor } from '../path';
import type { PlayerProgress } from '../progress';
import { COMBAT, type HitResult } from '../combat';
import { dist, type Entity, type PlayerCommand } from '../world';
import { duelRecoveryOf } from './duelRecovery';
import { shoveHeld } from './duelHolds';
import { duelAirOf } from './duelAir';
import { qinggongStats } from '../qinggong';
import { BOT_ACT_FILTERS, BOT_SKILL_GATES, newBotMem, thinkBot, type BotAct, type BotMem, type BotSkill, type FighterView } from './duelBot';
import { duelGroundOf } from './duelGround';
import { duelLockOf } from './duelLock';
import { pvpDuelOf } from './pvpDuel';
import { barSkillOk, skillBarOf } from './skillBar';
import type { SystemHost, WorldSystem } from './types';

/** Commands (part of `PlayerCommand`); the challenge itself is the ordinary `duelChallenge`. */
export type DuelMatchOp = 'rematch' | 'leave' | 'giveUp' | 'mirror';
export type DuelMatchCommand = { t: 'duelMatch'; op: DuelMatchOp };

/** A tournament bout (systems/tournament.ts): the opponent to fight at the ring, on fair-arena terms. */
export interface TourneyBout {
  readonly name: string;
  /** One of `DUEL_STYLES`' ids: the opponent's art and manner. */
  readonly styleId: string;
  /** The opponent's skill, 0-4. */
  readonly level: number;
  readonly sprite: string;
  /** Shown over the bout (Thai): the round, the stage. */
  readonly label: string;
  /** A show bout (after a bye): decides nothing. */
  readonly exhibition?: boolean;
}

export type MatchPhase = 'countdown' | 'wait' | 'fight' | 'roundEnd' | 'result';

export interface MatchState {
  phase: MatchPhase;
  /** Seconds left in this phase (the round clock while fighting). */
  t: number;
  /** 1-based round under way. */
  round: number;
  /** Rounds won: [me, the rival]. */
  wins: [number, number];
  meId: number;
  foeId: number;
  foeName: string;
  style: DuelStyle;
  level: number;
  /** The last round's winner: 0 me, 1 the rival, -1 a drawn round. */
  lastRound: 0 | 1 | -1 | null;
  /** The bout's winner once `result`: 0 me, 1 the rival, -1 a draw (the v2 bout's clock out at equal health). */
  winner: 0 | 1 | -1 | null;
  /** The bout is paused: the challenger stopped giving any input (the clock and the rival wait). */
  paused: boolean;
  /** The KO beat after a round (phase stays roundEnd for 0.5 s before its banner); the sim keeps stepping. */
  ko?: boolean;
  /** After a bout: this style's stars before and after, and the win streak. */
  rank: { from: number; to: number; streak: number } | null;
  /** A tournament bout: no rematch, the ladder of stars untouched; fair-arena numbers. */
  tourney?: { label: string; exhibition: boolean };
  /** Between two players (no stand-in): `meId` and `foeId` are the two, `names` theirs. */
  pvp: boolean;
  names: [string, string];
  /** The rival's martial art (its name), and whether it is a copy of the challenger's build. */
  foeArt: string;
  mirror: boolean;
  /** The ring: its middle and radius (metres). */
  ring: { x: number; y: number; r: number };
  /** This bout so far: blows landed, blows on the guard, guards broken, counters, for [me, the rival]. */
  stats: [BoutStats, BoutStats];
  /** The last counter: who made it (0 me, 1 the rival) and how many there have been (a counter to watch). */
  counter: { who: 0 | 1; n: number } | null;
  /** แต้มการ์ด for [me, the rival]: points, most, seconds left of a broken guard, seconds left of a counter stance. */
  guard: [GuardView, GuardView];
}

export interface BoutStats {
  hits: number;
  blocked: number;
  broken: number;
  counters: number;
}
const newStats = (): [BoutStats, BoutStats] => [
  { hits: 0, blocked: 0, broken: 0, counters: 0 },
  { hits: 0, blocked: 0, broken: 0, counters: 0 },
];

/** What a fighter was a moment ago, to see what a blow did. */
interface Prev {
  hp: number;
  stamina: number;
  stun: number;
  blocking: boolean;
  /** Cooldowns of the counter moves, to see one being pressed. */
  cd: Record<string, number>;
}

export interface GuardView {
  gp: number;
  max: number;
  brokenT: number;
  counterT: number;
}

interface GuardState extends GuardView {
  /** The World time (this system's clock) before which the guard points do not come back. */
  regenAt: number;
  /** The counter move whose stance is open. */
  counterId: string;
  /** The last time the other was seen winding up a break (it cuts through a stance). */
  breakSeen: number;
}
const newGuard = (): GuardState => ({ gp: DUEL_GUARD.max, max: DUEL_GUARD.max, brokenT: 0, counterT: 0, regenAt: 0, counterId: '', breakSeen: -9 });
const viewOfGuard = (g: GuardState): GuardView => ({ gp: g.gp, max: g.max, brokenT: g.brokenT, counterT: g.counterT });

const SYSTEMS = new WeakMap<object, DuelMatchSystem>();
export const duelMatchOf = (w: object): DuelMatchSystem | null => SYSTEMS.get(w) ?? null;

/** What the player may not do in the bout. */
const REFUSED = new Set<PlayerCommand['t']>(['mount', 'meditate', 'travel', 'pk', 'auto', 'fly', 'interact', 'enter']);

interface Shove {
  id: number;
  vx: number;
  vy: number;
  t: number;
}

export class DuelMatchSystem implements WorldSystem {
  readonly name = 'duelMatch';
  /** The rival standing at the hub (the NPC), or -1. */
  rivalId = -1;
  private state: MatchState | null = null;
  private away: Entity | null = null;
  private back = { x: 0, y: 0 };
  private centre = { x: 0, y: 0 };
  private marks: [{ x: number; y: number }, { x: number; y: number }] = [
    { x: 0, y: 0 },
    { x: 0, y: 0 },
  ];
  private bot: { mem: BotMem; progress: PlayerProgress; act: { move: string; block: boolean; engage: boolean } } | null =
    null;
  /** Stars (0-4) per rival style: the rival's skill; a win raises this style's, a loss lowers it. */
  private levels: Record<string, number> = {};
  private hpBase = new Map<number, number>();
  private pressed = false;
  private idleT = 0;
  private levelOf(styleId: string): number {
    if (this.tourney && styleId === this.tourney.styleId) return this.tourney.level;
    return this.levels[styleId] ?? 0;
  }
  private styleIx = 0;
  private nameIx = 0;
  private prev = new Map<number, Prev>();
  private guards = new Map<number, GuardState>();
  private guardSaid = 0;
  private recent = new Map<number, { t: number; d: number }[]>();
  private pvp = false;
  private done = false;
  /** Fight the challenger's own build (the mirror), else the rival's own. Off by default in the clone. */
  mirror = false;
  /** The tournament bout under way or being started (`startTourney`), else null. */
  private tourney: TourneyBout | null = null;
  /** Fair arena: each fighter's own numbers to put back, and the weight of the build they fight in. */
  private fairSaved = new Map<number, Record<string, number>>();
  private fairMul = new Map<number, { atk: number; hp: number }>();
  private get mirrorOn(): boolean {
    return this.mirror && !this.tourney;
  }

  private get radius(): number {
    return DUEL_FLAGS.v2 ? DUEL_V2.radius : DUEL_MATCH.radius;
  }

  private get gap(): number {
    return DUEL_FLAGS.v2 ? DUEL_V2.markGap : DUEL_MATCH.markGap;
  }
  private shoves: Shove[] = [];
  private resultAge = 0;
  private clock = 0;
  /** Bouts taken and won this session, shown on the result card. */
  record = { played: 0, won: 0, streak: 0 };

  constructor(private readonly h: SystemHost) {}

  readonly commands = {
    duelMatch: (c: { op: DuelMatchOp }) => this.op(c.op),
  };

  /** The bout under way, or null. */
  get current(): Readonly<MatchState> | null {
    return this.state;
  }

  get active(): boolean {
    return this.state !== null;
  }

  /** The style the rival at the hub will fight in next (shown on the challenge prompt). */
  get nextStyle(): DuelStyle {
    if (this.tourney) return DUEL_STYLES.find((x) => x.id === this.tourney!.styleId) ?? DUEL_STYLES[0]!;
    return DUEL_STYLES[this.styleIx % DUEL_STYLES.length]!;
  }

  get nextLevel(): number {
    return this.levelOf(this.nextStyle.id);
  }

  /** The martial art the next rival fights with (its name). */
  get nextArt(): string {
    return DUEL_FLAGS.v2 && !this.mirrorOn ? getArt(DUEL_BUILDS[this.nextStyle.id]!.art).name : 'ตามวิชาของท่าน';
  }

  /** Between two players: the result has been shown and the host may send both back. */
  get over(): boolean {
    return this.done;
  }

  setup(): void {
    const w = this.h.world;
    SYSTEMS.set(w, this);
    w.duelMod = (src, tgt, res, kind, skillId) => this.mod(src, tgt, res, kind, skillId);
    if (w.map.kind !== 'hub' || w.map.interior || !DUEL_FLAGS.rival) return;
    if (DUEL_FLAGS.v2) this.arena = this.findArena();
    this.styleIx = Math.floor(this.h.random() * DUEL_STYLES.length);
    this.nameIx = Math.floor(this.h.random() * DUEL_NAMES.length);
    this.placeRival();
  }

  /** The nearest floor round the hub rival's home that is clear all round a ring: nothing to stand in the way. */
  private findArena(): { x: number; y: number } | null {
    const m = this.h.world.map;
    const R = DUEL_V2.radius;
    // `deep`: also clear beyond the ring on the side the camera looks from (a roof there would hide the fighters).
    const clear = (x: number, y: number, deep: boolean): boolean => {
      if (!freeAt(m, x, y)) return false;
      for (let i = 0; i < 16; i++) {
        const a = (i / 16) * Math.PI * 2;
        const c = Math.cos(a);
        const s = Math.sin(a);
        if (!freeAt(m, x + c * R * 0.95, y + s * R * 0.95)) return false;
        if (i % 2 === 0 && !freeAt(m, x + c * R * 0.5, y + s * R * 0.5)) return false;
        if (deep && c + s > 0.2) for (const d of [R + 2.5, R + 5]) if (!freeAt(m, x + c * d, y + s * d)) return false;
      }
      return true;
    };
    // The 256 m plains: the bout is fought at the duel ring, not beside the wake spot at the south gate.
    const ringed = plains256() && m.zone === 'plains';
    const ox = ringed ? RING.at[0] : m.spawn.x + DUEL_MATCH.home[0];
    const oy = ringed ? RING.at[1] : m.spawn.y + DUEL_MATCH.home[1];
    for (const deep of [true, false])
      for (let r = 0; r <= 40; r += 2) {
        const n = Math.max(1, Math.round(r * 1.5));
        for (let k = 0; k < n; k++) {
          const a = (k / n) * Math.PI * 2;
          const x = Math.round(ox + Math.cos(a) * r);
          const y = Math.round(oy + Math.sin(a) * r);
          if (clear(x, y, deep)) return { x, y };
        }
      }
    return null;
  }

  private homeSpot(): { x: number; y: number } | null {
    const m = this.h.world.map;
    const s = m.spawn;

    const x = s.x + DUEL_MATCH.home[0];
    const y = s.y + DUEL_MATCH.home[1];
    if (freeAt(m, x, y)) return { x, y };
    const f = nearestFloor(m, x, y, 5);
    return f ? { x: f[0], y: f[1] } : null;
  }

  private placeRival(): void {
    const at = this.homeSpot();
    if (!at) return;
    const e = this.h.add({
      kind: 'npc',
      x: at.x,
      y: at.y,
      name: DUEL_NAMES[this.nameIx % DUEL_NAMES.length]!,
      title: `${this.nextStyle.name} · ท้าประลองได้`,
      sprite: ARENA_ROSTER[0]?.sprite ?? 'guardOfficer',
      facing: 1,
    });
    this.rivalId = e.id;
  }

  /** Blocks the challenge of the hub rival (ours); everything else is left to the rest. */
  refuse(cmd: PlayerCommand): boolean {
    const w = this.h.world;
    if (cmd.t === 'duelChallenge' && cmd.id === this.rivalId && this.rivalId >= 0 && w.player.id === w.primaryId) {
      this.start();
      return true;
    }
    // A broken guard cannot be raised.
    if (cmd.t === 'block' && cmd.on && this.state && DUEL_FLAGS.v2 && (this.guards.get(w.player.id)?.brokenT ?? 0) > 0) return true;
    if (this.state && !this.pvp && w.player.id === w.primaryId && REFUSED.has(cmd.t)) {
      this.h.notice('กำลังประลองอยู่');
      return true;
    }
    return false;
  }

  /** Why the player cannot start now (Thai), or null. */
  startBlock(): string | null {
    const w = this.h.world;
    const p = w.player;
    const r = w.get(this.rivalId);
    if (this.state) return 'อยู่ระหว่างประลองอยู่แล้ว';
    if (!r || r.dead) return 'ไม่พบคู่ประลอง';
    if (w.duel || w.pvpBout) return 'อยู่ระหว่างประลองอยู่แล้ว';
    if (p.dead || p.jailT > 0 || p.carrying != null) return 'ตอนนี้ประลองไม่ได้';
    if (p.combatT > 0) return 'ยังอยู่ในการต่อสู้';
    if (!this.tourney && dist(p, r) > DUEL_MATCH.reach) return 'อยู่ไกลเกินไป เดินเข้าไปใกล้คู่ประลองก่อน';
    return null;
  }

  // ───────────────────────────── the bout ─────────────────────────────

  private start(): void {
    const w = this.h.world;
    const why = this.startBlock();
    if (why) return this.h.notice(why);
    const p = w.player;
    const r = w.get(this.rivalId)!;
    this.h.stopMeditating();
    this.h.dismount();
    this.back = { x: p.x, y: p.y };
    const g = this.gap;
    if (DUEL_FLAGS.v2 && this.arena) {
      // The bouts are fought on the open floor found at the start, whoever stands where.
      this.centre = { ...this.arena };
      this.marks = [
        { x: this.arena.x - g, y: this.arena.y },
        { x: this.arena.x + g, y: this.arena.y },
      ];
    } else {
      // The middle is between the two; the marks lie along the line that joins them.
      const mid = this.snap((p.x + r.x) / 2, (p.y + r.y) / 2);
      this.centre = mid;
      const l = dist(p, r) || 1;
      const ux = (r.x - p.x) / l;
      const uy = (r.y - p.y) / l;
      this.marks = [this.snap(mid.x - ux * g, mid.y - uy * g), this.snap(mid.x + ux * g, mid.y + uy * g)];
    }
    // The rival steps out of the world for the bout and comes back after it.
    this.away = r;
    w.entities.delete(r.id);
    this.rivalId = -1;
    this.clearBystanders();

    const style = this.nextStyle;
    const foeName = this.tourney?.name ?? r.name;
    this.foeSprite = this.tourney?.sprite ?? r.sprite;
    const botId = this.spawnBot(foeName, style);

    this.state = {
      phase: 'countdown',
      t: DUEL_MATCH.countdownFirst,
      round: 1,
      wins: [0, 0],
      meId: p.id,
      foeId: botId,
      foeName,
      style,
      level: this.levelOf(style.id),
      lastRound: null,
      winner: null,
      paused: false,
      rank: null,
      pvp: false,
      ...(this.tourney ? { tourney: { label: this.tourney.label, exhibition: !!this.tourney.exhibition } } : {}),
      names: [p.name, foeName],
      foeArt: this.artName(style),
      mirror: this.mirrorOn || !DUEL_FLAGS.v2,
      ring: { x: this.centre.x, y: this.centre.y, r: this.radius },
      stats: newStats(),
      counter: null,
      guard: [viewOfGuard(newGuard()), viewOfGuard(newGuard())],
    };
    w.pvpBout = { a: p.id, b: botId, fight: false, ...(this.tourney ? { fair: true } : {}) };
    this.prev.clear();
    this.shoves = [];
    this.clock = 0;
    this.scaleHp(p);
    this.scaleBot(w.get(botId)!, p);
    if (this.tourney) {
      this.fairBegin(p);
      this.fairBegin(w.get(botId)!);
    }
    this.pressed = false;
    this.idleT = 0;
    this.resetRound();
    // Locked on the other fighter from the countdown (round 4).
    if (DUEL_FLAGS.v2) duelLockOf(w)?.set(p.id, botId);
    this.h.notice(`ท้าประลอง${foeName} · ${style.name}`);
    this.say('countdown');
  }

  /** Villagers stepped out of the ring for the bout (DUEL-RING MF2); they return when it ends. */
  private bystanders: Entity[] = [];
  private clearBystanders(): void {
    const w = this.h.world;
    const reach = this.radius + 3;
    this.bystanders = [];
    for (const e of [...w.entities.values()]) {
      if (e.kind !== 'npc' && e.kind !== 'ambient' && e.kind !== 'rival') continue;
      if (Math.hypot(e.x - this.centre.x, e.y - this.centre.y) > reach) continue;
      this.bystanders.push(e);
      w.entities.delete(e.id);
    }
  }

  private foeSprite = '';
  /** Round 2: the middle of an open stretch of floor near the spawn where the bouts are fought. */
  private arena: { x: number; y: number } | null = null;

  private artName(style: DuelStyle): string {
    return DUEL_FLAGS.v2 && !this.mirrorOn ? getArt(DUEL_BUILDS[style.id]!.art).name : getArt(this.h.world.progress.activeArt).name;
  }

  /** Puts the rival into the World as a second player: a copy of the challenger's build, or the style's own. */
  private spawnBot(name: string, style: DuelStyle): number {
    const w = this.h.world;
    const progress = structuredClone(w.progress) as PlayerProgress;
    Object.assign(progress, {
      name,
      title: style.name,
      pkPoints: 0,
      jailT: 0,
      party: [],
      karma: 'normal',
      arriveFrom: null,
    });
    if (DUEL_FLAGS.v2 && !this.mirrorOn) this.applyBuild(progress, style);
    const id = w.addPlayer(progress, this.marks[1]);
    const be = w.get(id)!;
    // The build's weight (data/duelMatch.ts): arts differ in what they give, this evens a style with the others.
    const wt = DUEL_FLAGS.v2 && !this.mirrorOn ? DUEL_BUILDS[style.id] : undefined;
    this.fairMul.set(id, { atk: wt?.atk ?? 1, hp: wt?.hp ?? 1 });
    if (wt?.hp) {
      be.maxHp = Math.round(be.maxHp * wt.hp);
      be.hp = be.maxHp;
    }
    if (wt?.atk) {
      be.atk *= wt.atk;
      be.magic *= wt.atk;
    }
    if (DUEL_FLAGS.v2 && !this.tourney) {
      const m = DUEL_V2.rivalAtk[Math.max(0, Math.min(DUEL_V2.rivalAtk.length - 1, this.levelOf(style.id)))]!;
      be.atk *= m;
      be.magic *= m;
    }
    be.sprite = this.foeSprite;
    be.name = name;
    be.title = style.name;
    this.bot = { mem: newBotMem(), progress, act: { move: '0,0', block: false, engage: false } };
    return id;
  }

  /** The style's own martial art and neigong, at levels set against the challenger's own and the rival's level. */
  private applyBuild(pr: PlayerProgress, style: DuelStyle): void {
    const b = DUEL_BUILDS[style.id];
    if (!b) return;
    const art = getArt(b.art);
    const mine = structuredClone(this.h.world.progress) as PlayerProgress;
    const ids = getArt(mine.activeArt).skills;
    const skillAvg = ids.reduce((t, id) => t + (mine.skillLevels[id] ?? 1), 0) / Math.max(1, ids.length);
    const ngMine = mine.neigong[mine.activeNeigong] ?? 1;
    const lv = this.levelOf(style.id) * 0.75;
    const skillLevel = Math.max(1, Math.round(skillAvg + b.skillDelta + lv));
    const ngLevel = Math.max(1, Math.min(getNeigong(b.neigong).maxLevel, Math.round(ngMine + b.neigongDelta + lv)));
    pr.arts = [b.art];
    pr.activeArt = b.art;
    pr.skillLevels = Object.fromEntries(art.skills.map((id) => [id, skillLevel]));
    pr.neigong = { [b.neigong]: ngLevel };
    pr.activeNeigong = b.neigong;
    // The stand-in knows the air dash (นกนางแอ่นพุ่งตัว 3): it jumps in and dives (P1.5).
    pr.qinggong = { ...pr.qinggong, 'swallow-dash': Math.max(3, mine.qinggong['swallow-dash'] ?? 0) };
    delete (pr as { skillBar?: unknown }).skillBar;
    delete (pr as { movesLearned?: unknown }).movesLearned;
    // A fist fighter holds no sword.
    if (!isArmed(art)) {
      const eq = { ...pr.equipment };
      delete eq.weapon;
      pr.equipment = eq;
    }
  }

  private snap(x: number, y: number): { x: number; y: number } {
    const m = this.h.world.map;
    if (freeAt(m, x, y)) return { x, y };
    const f = nearestFloor(m, x, y, 4);
    return f ? { x: f[0], y: f[1] } : { x: this.h.world.player.x, y: this.h.world.player.y };
  }

  /** Both whole, on their marks, facing each other. */
  private resetRound(): void {
    const s = this.state!;
    const w = this.h.world;
    const ids = [s.meId, s.foeId];
    ids.forEach((id, i) => {
      const e = w.get(id);
      if (!e) return;
      const m = this.marks[i]!;
      Object.assign(e, {
        x: m.x,
        y: m.y,
        z: 0,
        vz: 0,
        path: null,
        cast: null,
        targetId: id === s.meId ? s.foeId : s.meId,
        blocking: false,
        hp: e.maxHp,
        qi: e.maxQi,
        stamina: e.maxStamina,
        rage: 0,
        stunT: 0,
        invulnT: 0,
        swingT: 0,
        hitT: 0,
        attackT: 0,
        dashT: 0,
        spinT: 0,
        combatT: 0,
        cooldowns: {},
        statuses: [],
        dead: false,
        deadT: 0,
      });
      this.prev.set(id, { hp: e.maxHp, stamina: e.maxStamina, stun: 0, blocking: false, cd: {} });
      this.guards.set(id, newGuard());
    });
    const a = w.get(s.meId);
    const b = w.get(s.foeId);
    if (a && b) {
      this.h.face(a, b);
      this.h.face(b, a);
    }
    this.shoves = [];
    this.stopBot();
  }

  private say(phase: 'countdown' | 'fight' | 'result' | 'roundEnd'): void {
    const s = this.state!;
    this.h.emit({
      type: 'pvpDuel',
      phase,
      a: s.meId,
      b: s.foeId,
      names: s.names,
      ...(phase === 'result' ? { winner: s.winner === 0 ? s.meId : s.winner === 1 ? s.foeId : null } : {}),
      ...(phase === 'roundEnd' ? { winner: s.lastRound === 0 ? s.meId : s.lastRound === 1 ? s.foeId : null } : {}),
      ...(DUEL_FLAGS.v2 ? { round: s.round, wins: [s.wins[0], s.wins[1]] as [number, number], ring: [s.ring.x, s.ring.y, s.ring.r] as [number, number, number] } : {}),
    });
  }

  private op(op: DuelMatchOp): void {
    const w = this.h.world;
    if (op === 'mirror') {
      if (w.player.id === w.primaryId && !this.state) this.mirror = !this.mirror;
      return;
    }
    if (w.player.id !== w.primaryId || this.pvp) return;
    const s = this.state;
    if (!s) return;
    if (op === 'giveUp' && s.phase !== 'result') {
      s.wins[1] = DUEL_MATCH.winRounds;
      return this.endMatch();
    }
    if (op === 'rematch' && s.phase === 'result' && !this.tourney) return this.again();
    if (op === 'leave' && s.phase === 'result') return this.leave();
  }

  private again(): void {
    const s = this.state!;
    const w = this.h.world;
    // A fresh bout with the next rival style. With the rival's own builds it is a new body.
    this.styleIx = (this.styleIx + 1) % DUEL_STYLES.length;
    const style = this.nextStyle;
    this.stopBot();
    if (DUEL_FLAGS.v2) {
      w.removePlayer(s.foeId);
      s.foeId = this.spawnBot(s.foeName, style);
      this.scaleBot(w.get(s.foeId)!, w.get(s.meId)!);
      duelLockOf(w)?.set(s.meId, s.foeId);
    } else {
      const be = w.get(s.foeId);
      if (be) be.title = style.name;
      if (this.bot) this.bot.mem = newBotMem();
    }
    Object.assign(s, {
      phase: 'countdown' as MatchPhase,
      t: DUEL_MATCH.countdownFirst,
      round: 1,
      wins: [0, 0] as [number, number],
      style,
      level: this.levelOf(style.id),
      lastRound: null,
      winner: null,
      paused: false,
      rank: null,
      foeArt: this.artName(style),
      mirror: this.mirrorOn || !DUEL_FLAGS.v2,
      stats: newStats(),
      counter: null,
      guard: [viewOfGuard(newGuard()), viewOfGuard(newGuard())],
    });
    w.pvpBout = { a: s.meId, b: s.foeId, fight: false };
    this.prev.clear();
    this.clock = 0;
    this.pressed = false;
    this.idleT = 0;
    this.resetRound();
    this.say('countdown');
  }

  /** The bout is over and the player lets it go: the rival goes home, the player steps back. */
  private leave(): void {
    const w = this.h.world;
    const s = this.state!;
    const p = w.get(s.meId);
    w.pvpBout = null;
    duelLockOf(w)?.set(s.meId, null);
    this.stopBot();
    if (this.bot) w.removePlayer(s.foeId);
    this.bot = null;
    if (p) this.restoreHp(p);
    this.hpBase.clear();
    if (p) this.fairEnd(p);
    if (p) {
      Object.assign(p, {
        x: this.back.x,
        y: this.back.y,
        hp: p.maxHp,
        qi: p.maxQi,
        stamina: p.maxStamina,
        rage: 0,
        stunT: 0,
        cast: null,
        blocking: false,
        cooldowns: {},
        statuses: [],
        targetId: null,
        path: null,
      });
    }
    this.state = null;
    this.tourney = null;
    this.shoves = [];
    for (const b of this.bystanders) if (!w.entities.has(b.id)) w.entities.set(b.id, b);
    this.bystanders = [];
    // The rival takes the hub spot again, ready for the next challenge, with the next style in the title.
    const r = this.away;
    this.away = null;
    this.nameIx = (this.nameIx + 1) % DUEL_NAMES.length;
    if (r) {
      Object.assign(r, { name: DUEL_NAMES[this.nameIx]!, title: `${this.nextStyle.name} · ท้าประลองได้` });
      w.entities.set(r.id, r);
      this.rivalId = r.id;
    }
  }

  private endRound(winner: 0 | 1 | -1): void {
    const s = this.state!;
    s.lastRound = winner;
    if (winner === 0) s.wins[0]++;
    else if (winner === 1) s.wins[1]++;
    // Nobody falls: the blow that would have is a sliver; lay down the guard and the held keys, and put out bleeds and burns.
    this.stopBot();
    this.calm();
    // The KO beat first (not for a drawn round or a clock-out: nobody fell); the banner or the result follows.
    if (!this.pvp && DUEL_FLAGS.v2 && winner !== -1 && s.t > 0) {
      s.phase = 'roundEnd';
      s.ko = true;
      s.t = DUEL_MATCH.koBeat;
      this.h.world.pvpBout!.fight = false;
      return;
    }
    this.afterKo();
  }

  /** After the KO beat (or at once): the match is over, or the round's result shows. */
  private afterKo(): void {
    const s = this.state!;
    if (s.wins[0] >= DUEL_MATCH.winRounds || s.wins[1] >= DUEL_MATCH.winRounds || s.round >= DUEL_MATCH.maxRounds)
      return this.endMatch();
    s.phase = 'roundEnd';
    s.t = DUEL_MATCH.roundResult;
    this.h.world.pvpBout!.fight = false;
    if (DUEL_FLAGS.v2) this.say('roundEnd');
  }

  /** Between rounds nothing ticks on: statuses (a bleed) are put out and nobody is below 1 health. */
  private calm(): void {
    const s = this.state!;
    for (const id of [s.meId, s.foeId]) {
      const e = this.h.world.get(id);
      if (!e) continue;
      e.statuses = [];
      e.dead = false;
      if (e.hp < 1) e.hp = 1;
    }
  }

  private endMatch(): void {
    const s = this.state!;
    const w = this.h.world;
    s.winner = s.wins[0] >= s.wins[1] ? 0 : 1;
    if (s.wins[0] === s.wins[1]) s.winner = DUEL_FLAGS.v2 ? -1 : 0; // the one-bout format: a draw (DUEL_FORMAT)
    this.calm();
    s.phase = 'result';
    s.t = this.pvp ? DUEL_V2.pvpResult : DUEL_MATCH.resultSeconds;
    this.resultAge = 0;
    w.pvpBout!.fight = false;
    this.stopBot();
    if (!this.pvp && !this.tourney) {
      this.record.played++;
      const id = s.style.id;
      const from = this.levelOf(id);
      if (s.winner === 0) {
        this.record.won++;
        this.record.streak++;
        this.levels[id] = Math.min(DUEL_MATCH.maxLevel, from + 1);
      } else if (s.winner === 1) {
        this.record.streak = 0;
        this.levels[id] = Math.max(0, from - 1);
      }
      s.rank = { from, to: this.levels[id]!, streak: this.record.streak };
    }
    // Both whole at the end, as in a duel between players.
    for (const id of [s.meId, s.foeId]) {
      const e = w.get(id);
      if (e) Object.assign(e, { hp: e.maxHp, qi: e.maxQi, stamina: e.maxStamina, cast: null, blocking: false, stunT: 0 });
    }
    for (const [id, who] of [[s.meId, 0], [s.foeId, 1]] as const)
      resultPose(w.get(id), s.winner === -1 ? 'draw' : s.winner === who ? 'win' : 'lose', (ev) => this.h.emit(ev));
    if (!this.pvp)
      this.h.log('sys', `${s.winner === 0 ? 'ชนะ' : s.winner === 1 ? 'แพ้' : 'เสมอ'}ประลอง ${s.wins[0]}-${s.wins[1]} · ${s.foeName}`);
    this.say('result');
  }

  // ───────────────────────────── between two players ─────────────────────────────

  /**
   * Two real players in a private World (the zone server's duel, as systems/pvpDuel.ts but best of three): both are
   * players of this World, set on the ring's marks (or either side of the spawn). False when it cannot be.
   */
  begin(aId: number, bId: number): boolean {
    const w = this.h.world;
    const a = w.get(aId);
    const b = w.get(bId);
    if (this.state || aId === bId || a?.kind !== 'player' || b?.kind !== 'player') return false;
    // The ring is for the two of them: no monsters, no rival.
    for (const e of [...w.entities.values()]) if (e.kind === 'monster' || e.id === this.rivalId) w.entities.delete(e.id);
    this.rivalId = -1;
    const ring = duelGroundOf(w)?.ring;
    const sp = w.map.spawn;
    const g = this.gap;
    const ar = DUEL_FLAGS.v2 ? this.arena : null;
    const m: readonly [readonly [number, number], readonly [number, number]] = ring
      ? ring.marks
      : ar
        ? [
            [ar.x - g, ar.y],
            [ar.x + g, ar.y],
          ]
        : [
          [sp.x - g, sp.y],
          [sp.x + g, sp.y],
        ];
    this.marks = [
      { x: m[0][0], y: m[0][1] },
      { x: m[1][0], y: m[1][1] },
    ];
    this.centre = { x: (m[0][0] + m[1][0]) / 2, y: (m[0][1] + m[1][1]) / 2 };
    this.pvp = true;
    this.done = false;
    this.bot = null;
    this.state = {
      phase: 'countdown',
      t: DUEL_MATCH.countdownFirst,
      round: 1,
      wins: [0, 0],
      meId: aId,
      foeId: bId,
      foeName: b.name,
      style: DUEL_STYLES[0]!,
      level: 0,
      lastRound: null,
      winner: null,
      paused: false,
      rank: null,
      pvp: true,
      names: [a.name, b.name],
      foeArt: '',
      mirror: true,
      ring: { x: this.centre.x, y: this.centre.y, r: this.radius },
      stats: newStats(),
      counter: null,
      guard: [viewOfGuard(newGuard()), viewOfGuard(newGuard())],
    };
    w.pvpBout = { a: aId, b: bId, fight: false };
    this.prev.clear();
    this.shoves = [];
    this.clock = 0;
    this.scaleHp(a);
    this.scaleHp(b);
    if (DUEL_FLAGS.v2) {
      duelLockOf(w)?.set(aId, bId);
      duelLockOf(w)?.set(bId, aId);
    }
    this.resetRound();
    this.say('countdown');
    return true;
  }

  /** `id` gives the bout away (dropped, gave up): the other wins. */
  forfeit(id: number): void {
    const s = this.state;
    if (!s || s.phase === 'result' || (id !== s.meId && id !== s.foeId)) return;
    if (id === s.meId) s.wins[1] = DUEL_MATCH.winRounds;
    else s.wins[0] = DUEL_MATCH.winRounds;
    this.endMatch();
  }

  private finishPvp(): void {
    this.h.world.pvpBout = null;
    this.state = null;
    this.pvp = false;
    this.done = true;
  }

  // ───────────────────────────── the rival's hands ─────────────────────────────

  /** Lays the rival's held controls down. */
  private stopBot(): void {
    const s = this.state;
    const b = this.bot;
    if (!s || !b) return;
    const w = this.h.world;
    w.apply({ t: 'moveDir', dx: 0, dy: 0 }, s.foeId);
    w.apply({ t: 'block', on: false }, s.foeId);
    const e = w.get(s.foeId);
    if (e) {
      const inp = w.players.get(s.foeId)?.input;
      if (inp) inp.engage = false;
    }
    b.act = { move: '0,0', block: false, engage: false };
  }

  /** Spends a fighter's guard points (a lock-form escape, systems/duelLockOpen.ts): false, and nothing spent, when short. */
  spendGuard(id: number, n: number): boolean {
    const g = this.guards.get(id) ?? newGuard();
    if (g.brokenT > 0 || g.gp < n) return false;
    this.guards.set(id, g);
    g.gp -= n;
    g.regenAt = this.clock + DUEL_GUARD.regenDelay;
    return true;
  }

  /** Seconds left of a fighter's broken guard (0: not broken). */
  brokenLeft(id: number): number {
    return this.guards.get(id)?.brokenT ?? 0;
  }

  private view(e: Entity): FighterView {
    return {
      x: e.x,
      y: e.y,
      hp: e.hp,
      maxHp: e.maxHp,
      stamina: e.stamina,
      maxStamina: e.maxStamina,
      gp: this.guards.get(e.id)?.gp ?? DUEL_GUARD.max,
      gpMax: DUEL_GUARD.max,
      brokenT: this.guards.get(e.id)?.brokenT ?? 0,
      counterT: this.guards.get(e.id)?.counterT ?? 0,
      blocking: e.blocking,
      stunT: e.stunT,
      swingT: e.swingT,
      dashT: e.dashT,
      recovery: duelRecoveryOf(this.h.world)?.left(e.id) ?? 0,
      z: e.z,
      diving: !!duelAirOf(this.h.world)?.dive(e.id),
      airDashed: e.airDashed,
      cast: e.cast
        ? { kind: e.cast.kind, left: Math.max(0, e.cast.windup - e.cast.t), big: e.cast.kind === 'attack' && bigMove(getSkill(e.cast.skillId)), ult: (getSkill(e.cast.skillId).rage ?? 0) >= RAGE.max }
        : null,
    };
  }

  private botSkills(me: Entity): BotSkill[] {
    const b = this.bot!;
    const bar = skillBarOf(b.progress);
    const out: BotSkill[] = [];
    bar.forEach((id, slot) => {
      if (!id || !barSkillOk(b.progress, id) || moveLocked(b.progress, id)) return;
      const s = getSkill(id);
      out.push({
        slot,
        id,
        kind: s.kind,
        range: s.range || (s.aoe ?? 0),
        windup: s.windup,
        cooldown: s.cooldown,
        power: s.power,
        dash: !!s.dash,
        counter: s.kind === 'buff' && id in DUEL_COUNTERS,
        ultimate: (s.rage ?? 0) >= RAGE.max,
        active: !!s.self && stacksOf(me.statuses, s.self.status) > 0,
        ready: (me.cooldowns[id] ?? 0) <= 0 && me.qi >= s.qi && me.rage >= (s.rage ?? 0) && BOT_SKILL_GATES.every((g) => g(this.h.world, me.id, id)),
      });
    });
    return out;
  }

  /** Runs the rival's mind and sends what it chose as that player's commands. */
  private drive(dt: number): void {
    const s = this.state!;
    const b = this.bot!;
    const w = this.h.world;
    const me = w.get(s.foeId);
    const foe = w.get(s.meId);
    if (!me || !foe) return;
    const act = thinkBot(
      {
        t: this.clock,
        me: this.view(me),
        foe: this.view(foe),
        skills: this.botSkills(me),
        canDash: me.stamina >= 25,
        canEscape: me.stamina >= DUEL_MOVE.escape.cost,
        canAirDash: me.stamina >= 25 + 12 && qinggongStats(b.progress.qinggong).dash.air,
        reach: familyOf(getArt(b.progress.activeArt)).botReach,
        floor: familyOf(getArt(b.progress.activeArt)).botFloor,
      },
      b.mem,
      s.style,
      s.level,
      () => this.h.random(),
    );
    void dt;
    if (act) this.send(BOT_ACT_FILTERS.reduce((a, f) => f(w, me.id, a), act), me);
  }

  private send(a: BotAct, me: Entity): void {
    const s = this.state!;
    const b = this.bot!;
    const w = this.h.world;
    const id = s.foeId;
    if (a.block !== b.act.block) w.apply({ t: 'block', on: a.block }, id);
    const mv = `${a.move[0].toFixed(2)},${a.move[1].toFixed(2)}`;
    if (mv !== b.act.move) w.apply({ t: 'moveDir', dx: a.move[0], dy: a.move[1] }, id);
    if (a.engage && !b.act.engage) w.apply({ t: 'engage', id: s.meId }, id);
    if (!a.engage && b.act.engage && mv === '0,0') {
      // Stop swinging without losing the target.
      const inp = w.players.get(id)?.input;
      if (inp) inp.engage = false;
      me.targetId = s.meId;
    }
    if (a.skill !== null) {
      me.targetId = s.meId;
      w.apply({ t: 'skill', slot: a.skill }, id);
    }
    if (a.jump) w.apply({ t: 'jump' }, id);
    if (a.attack) w.apply({ t: 'attack' }, id);
    if (a.dash) w.apply({ t: 'dash', dx: a.dash[0], dy: a.dash[1] }, id);
    b.act = { move: mv, block: a.block, engage: a.engage };
  }

  // ───────────────────────────── the clock ─────────────────────────────

  step(dt: number): void {
    const s = this.state;
    if (!s) {
      // The hub rival shows a health like a fighter's (not 1/1) on the target frame.
      const r = DUEL_FLAGS.v2 ? this.h.world.get(this.rivalId) : undefined;
      if (r) {
        const hp = Math.round(this.h.world.player.maxHp * DUEL_V2.hpMult);
        if (r.maxHp !== hp) Object.assign(r, { maxHp: hp, hp });
      }
      return;
    }
    const w = this.h.world;
    this.clock += dt;
    const me = w.get(s.meId);
    const be = w.get(s.foeId);
    if (!me || !be) return s.phase === 'result' ? undefined : this.endMatch();
    this.keepInRing(me);
    this.keepInRing(be);
    this.runShoves(dt);
    if (s.phase === 'countdown') {
      // From round 2 any input skips the countdown (the held keys of the last round count).
      const skip = s.round > 1 && !this.pvp && this.touched(s.meId);
      if ((s.t -= dt) > 0 && !skip) return;
      // Against the stand-in the first round waits for the challenger's first input; later rounds start at once.
      s.phase = this.pvp || !DUEL_FLAGS.v2 || s.round > 1 ? 'fight' : 'wait';
      s.t = DUEL_MATCH.roundSeconds;
      me.targetId = be.id;
      be.targetId = me.id;
      this.idleT = 0;
      this.pressed = false;
      if (s.phase === 'fight') {
        w.pvpBout!.fight = true;
        this.say('fight');
      }
      return;
    }
    if (s.phase === 'wait') {
      this.upkeep(me, dt);
      if (!this.touched(s.meId)) return;
      s.phase = 'fight';
      w.pvpBout!.fight = true;
      this.say('fight');
      return;
    }
    if (s.phase === 'roundEnd' && s.ko) {
      if ((s.t -= dt) > 0) return;
      s.ko = false;
      this.afterKo();
      return;
    }
    if (s.phase === 'roundEnd') {
      if ((s.t -= dt) > 0) return;
      s.round++;
      s.phase = 'countdown';
      s.t = DUEL_MATCH.countdownNext;
      this.resetRound();
      this.say('countdown');
      return;
    }
    if (s.phase === 'result') {
      this.resultAge += dt;
      if (this.resultAge < 2.5)
        for (const [id, who] of [[s.meId, 0], [s.foeId, 1]] as const)
          holdResultPose(this.h.world.get(id), s.winner === -1 ? 'draw' : s.winner === who ? 'win' : 'lose', (ev) => this.h.emit(ev));
      if ((s.t -= dt) <= 0) {
        if (this.pvp) this.finishPvp();
        else this.leave();
      }
      return;
    }
    // fight: the challenger gone quiet pauses it (the clock and the rival wait, nobody can hurt anybody)
    if (DUEL_FLAGS.v2 && !this.pvp) {
      const active = this.touched(s.meId) || me.blocking || me.dashT > 0;
      this.idleT = active ? 0 : this.idleT + dt;
      if (s.paused && active) {
        s.paused = false;
        w.pvpBout!.fight = true;
      } else if (!s.paused && this.idleT >= DUEL_V2.idlePause && s.t < DUEL_MATCH.roundSeconds - DUEL_V2.pauseGrace) {
        s.paused = true;
        w.pvpBout!.fight = false;
        this.stopBot();
        this.calm();
      }
      if (s.paused) return;
    }
    this.enforceHp(me);
    this.enforceHp(be);
    if (this.tourney) {
      this.fairHold(me);
      this.fairHold(be);
    }
    this.separate(me, be);
    if (DUEL_FLAGS.v2) {
      // From round 2 the blows are resolved in `mod` (before the event); here the counters answer, the guards and stances run.
      this.watch(me);
      this.watch(be);
      this.runCounters();
      this.upkeep(me, dt);
      this.upkeep(be, dt);
      this.publishGuards();
    } else {
      this.feel(me, be, 0);
      this.feel(be, me, 1);
      for (const e of [me, be]) {
        const p = this.prev.get(e.id);
        if (p) Object.assign(p, { hp: e.hp, stamina: e.stamina, stun: e.stunT, blocking: e.blocking });
      }
    }
    if (this.bot) this.drive(dt);
    s.t -= dt;
    const lost = (e: Entity) => e.hp <= e.maxHp * DUEL_MATCH.floorHp;
    const share = (e: Entity) => e.hp / e.maxHp;
    const lm = lost(me);
    const lb = lost(be);
    if (lm || lb) return this.endRound(lm && lb ? (share(me) >= share(be) ? 0 : 1) : lm ? 1 : 0);
    if (s.t <= 0) this.endRound(share(me) === share(be) ? -1 : share(me) > share(be) ? 0 : 1);
  }

  // ───────────────────────────── the tournament's fair arena ─────────────────────────────

  /**
   * Starts a tournament bout at the ring against the stand-in (the hub rival steps out and a body with `o`'s name and
   * manner takes its place). The player need not stand near the rival. Null when it started, else the reason (Thai).
   */
  startTourney(o: TourneyBout): string | null {
    if (this.state) return 'อยู่ระหว่างประลองอยู่แล้ว';
    this.tourney = o;
    const why = this.startBlock();
    if (why) {
      this.tourney = null;
      return why;
    }
    this.start();
    if (!this.state) {
      this.tourney = null;
      return 'เริ่มนัดไม่ได้';
    }
    return null;
  }

  /** Whether a tournament bout is under way. */
  get tourneyActive(): boolean {
    return !!this.tourney && !!this.state;
  }

  /** A fighter's view as the rival's mind sees it (the bot-vs-bot simulation, systems/duelSim.ts). */
  fighterView(id: number): FighterView | null {
    const e = this.h.world.get(id);
    return e ? this.view(e) : null;
  }

  private fairBegin(e: Entity): void {
    const F = FAIR_ARENA;
    const mul = this.fairMul.get(e.id) ?? { atk: 1, hp: 1 };
    const keys = ['atk', 'magic', 'def', 'magicDef', 'crit', 'magicCrit', 'hit', 'magicHit', 'dodge', 'toughness', 'maxHp'] as const;
    const saved: Record<string, number> = {};
    for (const k of keys) saved[k] = e[k];
    saved.maxHp = this.hpBase.get(e.id) ?? e.maxHp;
    this.fairSaved.set(e.id, saved);
    this.hpBase.set(e.id, Math.round(F.maxHp * mul.hp));
    e.maxHp = Math.round(F.maxHp * mul.hp * DUEL_V2.hpMult);
    e.hp = e.maxHp;
    this.fairHold(e);
  }

  /** The same base numbers every tick (the World recalculates now and then). */
  private fairHold(e: Entity): void {
    const F = FAIR_ARENA;
    const m = this.fairMul.get(e.id)?.atk ?? 1;
    Object.assign(e, {
      atk: F.atk * m,
      magic: F.magic * m,
      def: F.def,
      magicDef: F.magicDef,
      crit: F.crit,
      magicCrit: F.magicCrit,
      hit: F.hit,
      magicHit: F.magicHit,
      dodge: F.dodge,
      toughness: F.toughness,
    });
  }

  private fairEnd(e: Entity): void {
    const saved = this.fairSaved.get(e.id);
    if (!saved) return;
    Object.assign(e, saved);
    e.hp = e.maxHp;
    this.fairSaved.clear();
    this.fairMul.clear();
  }

  /** Round 4: both fighters have `hpMult` times their health in a bout (rounds of 15-30 s); the base is put back after. */
  private scaleHp(e: Entity): void {
    if (!DUEL_FLAGS.v2) return;
    if (!this.hpBase.has(e.id)) this.hpBase.set(e.id, e.maxHp);
    e.maxHp = Math.round(this.hpBase.get(e.id)! * DUEL_V2.hpMult);
    e.hp = Math.min(e.hp, e.maxHp);
  }

  /** Quick bouts vs the bot (user, 2026-10-11): equal health, the difficulty is the bot's skill. The tournament's fair arena sets its own. */
  private scaleBot(bot: Entity, me: Entity): void {
    if (DUEL_FLAGS.v2 && !this.tourney) {
      this.hpBase.set(bot.id, this.hpBase.get(me.id) ?? me.maxHp);
      bot.maxHp = Math.round(this.hpBase.get(bot.id)! * DUEL_V2.hpMult);
      bot.hp = bot.maxHp;
    }
    this.scaleHp(bot);
  }

  /** The World recalculates stats now and then (a buff, gear): put the bout's health back, keeping the share. */
  private enforceHp(e: Entity): void {
    const base = this.hpBase.get(e.id);
    if (base === undefined) return;
    const want = Math.round(base * DUEL_V2.hpMult);
    if (e.maxHp === want) return;
    const share = e.hp / Math.max(1, e.maxHp);
    e.maxHp = want;
    e.hp = Math.max(1, Math.min(want, Math.round(share * want * 1000) / 1000));
    const p = this.prev.get(e.id);
    if (p) p.hp = e.hp;
  }

  private restoreHp(e: Entity): void {
    const base = this.hpBase.get(e.id);
    if (base === undefined) return;
    e.maxHp = base;
    e.hp = base;
    this.hpBase.delete(e.id);
  }

  /** Whether the challenger gave any input since the last look (a key, a move, a guard). */
  private touched(id: number): boolean {
    const inp = this.h.world.players.get(id)?.input;
    const t = !!inp && (inp.dx !== 0 || inp.dy !== 0 || inp.block || inp.engage || inp.sprint);
    const p = this.pressed;
    this.pressed = false;
    return t || p;
  }

  /** A small push apart so the two sprites do not stack. */
  private separate(a: Entity, b: Entity): void {
    if (!DUEL_FLAGS.v2) return;
    const d = dist(a, b);
    if (d >= DUEL_V2.apart) return;
    const l = d || 1;
    const nx = d ? (a.x - b.x) / l : 1;
    const ny = d ? (a.y - b.y) / l : 0;
    const push = Math.min(0.015, (DUEL_V2.apart - d) / 2);
    this.h.move(a, nx * push, ny * push);
    this.h.move(b, -nx * push, -ny * push);
  }

  /** The ring has no wall to see; past its edge a fighter is eased back. */
  private keepInRing(e: Entity): void {
    if (DUEL_FLAGS.v2 && DUEL_FORMAT.ringOut) return; // the one-bout format: the edge is a line, not a wall (systems/duelRingOut.ts)
    const d = dist(e, this.centre);
    const over = d - this.radius;
    if (over <= 0) return;
    const nx = (this.centre.x - e.x) / d;
    const ny = (this.centre.y - e.y) / d;
    // Back onto the edge, exactly: the outward part of a walk is taken away and the along-the-edge part is left whole (no
    // slowing, no sliding back), so the edge is a wall that is slid along and never a spring.
    this.h.move(e, nx * over, ny * over);
  }

  /** Round 1 only (no guard system): what a blow did to `e` this tick, read from the change in health and พลังกาย: a shove and the tally. */
  private feel(e: Entity, other: Entity, idx: 0 | 1): void {
    const s = this.state!;
    const p = this.prev.get(e.id) ?? { hp: e.hp, stamina: e.stamina, stun: e.stunT, blocking: e.blocking, cd: {} };
    const k = DUEL_MATCH.knock;
    const lost = p.hp - e.hp;
    const tally = s.stats[idx];
    const theirs = s.stats[1 - idx];
    if (lost >= e.maxHp * 0.02 || p.stamina - e.stamina > 0.5) {
      const drained = p.stamina - e.stamina > 0.5 && e.blocking;
      if (drained) tally.blocked++;
      else theirs.hits++;
      let m = drained ? k.blocked : e.stunT > 0.4 ? k.broken : k.hit;
      if (lost / e.maxHp >= DUEL_MATCH.heavy) m *= 1.4;
      this.shove(e, other, m, k.seconds);
    }
  }

  /** A break in a fighter's hands (its wind-up is seen) cuts through a counter stance for a moment. */
  private watch(e: Entity): void {
    const g = this.guards.get(e.id);
    if (g && e.cast?.kind === 'break') g.breakSeen = this.clock;
  }

  /** Counter blows queued by `mod` (a stance undid a blow): they land the next tick. */
  private counterQ: { by: number; foe: number; id: string }[] = [];
  private runCounters(): void {
    if (!this.counterQ.length) return;
    const w = this.h.world;
    for (const c of this.counterQ.splice(0)) {
      const by = w.get(c.by);
      const foe = w.get(c.foe);
      const d = DUEL_COUNTERS[c.id];
      if (by && foe && d && !by.dead && !foe.dead) this.h.hit(by, foe, d.power, 'attack', c.id, d.stun);
    }
  }

  /**
   * The bout's rules for a blow between its two fighters (round 2 on), run by the World BEFORE the blow is applied and its
   * `hit` event is sent (`World.duelMod`), so the number, the blood and the hit-stop are what really happened:
   * - a counter stance open (not against a ท่าทำลาย, which cuts through): the blow is undone (`countered`, no damage, no stun)
   *   and answered next tick;
   * - a ท่าทำลาย on a guard: the guard breaks at once (the World's `guardBreak`); พลังกาย is not spent;
   * - a guarded blow: damage cut to `DUEL_GUARD.through` of it, guard points drained by its weight (none in a lock form,
   *   which adds ความโกรธ instead); at zero points the guard shatters (`guardShatter`: a stagger, then no guard for a while);
   * - the burst cap: a second's losses are at most `DUEL_V2.burst` of the bar (the number shown is the clamped one);
   * - the shove and the tally.
   */
  private mod(src: Entity, tgt: Entity, res: HitResult, kind: SkillKind, skillId: string): void {
    void skillId;
    const s = this.state;
    if (!s || s.phase !== 'fight' || !DUEL_FLAGS.v2 || res.outcome === 'dodge') return;
    const idx: 0 | 1 = tgt.id === s.meId ? 0 : 1;
    const k = DUEL_V2.knock;
    const tally = s.stats[idx];
    const theirs = s.stats[1 - idx];
    const g = this.guards.get(tgt.id) ?? newGuard();
    this.guards.set(tgt.id, g);
    const og = this.guards.get(src.id);
    const cutThrough = kind === 'break' || this.clock - (og?.breakSeen ?? -9) < 0.9;
    if (g.counterT > 0 && !cutThrough) {
      const c = DUEL_COUNTERS[g.counterId];
      Object.assign(res, { outcome: 'countered', damage: 0, stun: 0, staminaDamage: 0, crit: false });
      g.counterT = 0;
      tally.counters++;
      s.counter = { who: idx, n: (s.counter?.n ?? 0) + 1 };
      this.h.emit({ type: 'pvpDuel', phase: 'counter', a: s.meId, b: s.foeId, names: s.names, parry: tgt.id });
      if (c) this.counterQ.push({ by: tgt.id, foe: src.id, id: g.counterId });
      this.shove(src, tgt, k.parried, k.seconds);
      return;
    }
    // A break on a foe who is not guarding nor staggered is weak (the bluff): the triangle wants it read, not spammed.
    if (kind === 'break' && res.outcome === 'hit' && !tgt.blocking && tgt.stunT <= 0)
      res.damage = Math.max(1, Math.round(res.damage * DUEL_TRI.breakOpenMult));
    // Rage: a blow that lands feeds the one who landed it.
    if ((res.outcome === 'hit' || res.outcome === 'guardBreak' || res.outcome === 'interrupt') && src.maxRage)
      src.rage = Math.min(src.maxRage, src.rage + DUEL_RAGE.landed);
    if (res.outcome === 'guardBreak') {
      res.staminaDamage = 0;
      this.breakGuard(tgt, src, g, idx);
      theirs.hits++;
      this.shove(tgt, src, k.broken, k.seconds);
    } else if (res.outcome === 'blocked') {
      const weight = res.staminaDamage;
      res.staminaDamage = 0;
      res.damage = Math.max(1, Math.round((res.damage * DUEL_GUARD.through) / COMBAT.blockDamageMult));
      const inForm = duelLockOf(this.h.world)?.inForm(tgt.id) ?? false;
      tally.blocked++;
      if (inForm) tgt.rage = Math.min(tgt.maxRage, tgt.rage + DUEL_LOCK.formRage);
      else {
        // Guarding feeds rage more than being hit (the world adds its own blow-taken rage after this: 4, or 2 in a bout).
        tgt.rage = Math.min(tgt.maxRage, tgt.rage + DUEL_RAGE.guarded);
        g.gp -= weight * DUEL_GUARD.perPower;
        g.regenAt = this.clock + DUEL_GUARD.regenDelay;
      }
      this.shove(tgt, src, k.blocked, k.seconds);
      if (!inForm && g.gp <= 0) {
        res.outcome = 'guardShatter';
        res.stun = Math.max(res.stun, DUEL_GUARD.stagger);
        this.breakGuard(tgt, src, g, idx);
      }
    } else if (res.damage > 0) {
      theirs.hits++;
      let m = res.stun > 0.4 ? k.broken : k.hit;
      if (res.damage / tgt.maxHp >= DUEL_MATCH.heavy) m *= 1.4;
      this.shove(tgt, src, m, k.seconds);
    }
    if (res.damage > 0) {
      // The burst cap: a second's losses are at most `burst` of the bar.
      const win = (this.recent.get(tgt.id) ?? []).filter((x) => this.clock - x.t < 1);
      const taken = win.reduce((t, x) => t + x.d, 0);
      res.damage = Math.min(res.damage, Math.max(0, Math.floor(tgt.maxHp * DUEL_V2.burst - taken)));
      // The cap ate the whole blow: nothing lands, so no stagger or status rides on a "0".
      if (res.damage <= 0) {
        res.spent = true;
        res.stun = 0;
      }
      if (res.damage > 0) win.push({ t: this.clock, d: res.damage });
      this.recent.set(tgt.id, win);
    }
  }

  /** The guard breaks: a stagger, then no guard for `brokenSeconds`. */
  private breakGuard(e: Entity, other: Entity, g: GuardState, idx: 0 | 1): void {
    void other;
    const s = this.state!;
    g.gp = 0;
    g.brokenT = DUEL_GUARD.brokenSeconds;
    e.stunT = Math.max(e.stunT, DUEL_GUARD.stagger);
    e.blocking = false;
    const inp = this.h.world.players.get(e.id)?.input;
    if (inp) inp.block = false;
    s.stats[1 - idx].broken++;
  }

  /** Seconds left of a fighter's counter stance (0: none). */
  stanceOf(id: number): number {
    return this.guards.get(id)?.counterT ?? 0;
  }

  /** The state's guard view (and, between two players, a throttled `guard` event: the snapshot carries no guard points). */
  private publishGuards(): void {
    const s = this.state!;
    const a = this.guards.get(s.meId);
    const b = this.guards.get(s.foeId);
    if (!a || !b) return;
    s.guard = [viewOfGuard(a), viewOfGuard(b)];
    if (this.pvp && this.clock - this.guardSaid >= 0.25) {
      this.guardSaid = this.clock;
      this.h.emit({
        type: 'pvpDuel',
        phase: 'guard',
        a: s.meId,
        b: s.foeId,
        names: s.names,
        gp: [Math.round(a.gp), Math.round(b.gp)],
        broken: [Math.round(a.brokenT * 10) / 10, Math.round(b.brokenT * 10) / 10],
      });
    }
  }

  /** Per tick, after the blows: guard points come back, a broken guard stays down, stances open and run out. */
  private upkeep(e: Entity, dt: number): void {
    const g = this.guards.get(e.id);
    const p = this.prev.get(e.id);
    if (!g || !p) return;
    if (g.brokenT > 0) {
      g.brokenT = Math.max(0, g.brokenT - dt);
      e.blocking = false;
      const inp = this.h.world.players.get(e.id)?.input;
      if (inp) inp.block = false;
      if (g.brokenT === 0) g.gp = DUEL_GUARD.recoverTo;
    } else if (!e.blocking && this.clock >= g.regenAt) g.gp = Math.min(g.max, g.gp + DUEL_GUARD.regen * dt);
    // A guard costs no พลังกาย: never let it run dry under a guard.
    if (e.blocking && e.stamina < DUEL_GUARD.staminaFloor) e.stamina = DUEL_GUARD.staminaFloor;
    if (g.counterT > 0) g.counterT = Math.max(0, g.counterT - dt);
    // Health never below 1 and never above its bout size (a bleed or a burn must not kill between rounds).
    if (e.hp < 1) e.hp = 1;
    e.dead = false;
    // A move pressed (any cooldown that just started): it is one blow, so the basic cut is held back a moment; and it
    // counts as input.
    for (const id of Object.keys(e.cooldowns)) {
      const cd = e.cooldowns[id] ?? 0;
      if (cd > (p.cd[id] ?? 0) + 0.5) {
        e.attackT = Math.max(e.attackT, DUEL_V2.skillGap);
        if (e.id === this.state!.meId) this.pressed = true;
      }
    }
    // A counter move pressed (its cooldown just started): the stance opens.
    for (const id of Object.keys(DUEL_COUNTERS)) {
      const cd = e.cooldowns[id] ?? 0;
      if (cd >= 5 && cd > (p.cd[id] ?? 0) + 3) {
        g.counterT = DUEL_COUNTERS[id]!.window;
        g.counterId = id;
        this.h.emit({ type: 'pvpDuel', phase: 'stance', a: this.state!.meId, b: this.state!.foeId, names: this.state!.names, parry: e.id, window: DUEL_COUNTERS[id]!.window });
      }
      p.cd[id] = cd;
    }
    for (const id of Object.keys(e.cooldowns)) p.cd[id] = e.cooldowns[id] ?? 0;
    p.hp = e.hp;
    p.stamina = e.stamina;
    p.stun = e.stunT;
    p.blocking = e.blocking;
  }

  private shove(e: Entity, from: Entity, metres: number, seconds: number): void {
    if (shoveHeld(this.h.world, e.id)) return; // armour or a throw (systems/duelHolds.ts)
    const d = dist(e, from) || 1;
    this.shoves = this.shoves.filter((s) => s.id !== e.id);
    this.shoves.push({
      id: e.id,
      vx: ((e.x - from.x) / d) * (metres / seconds),
      vy: ((e.y - from.y) / d) * (metres / seconds),
      t: seconds,
    });
  }

  private runShoves(dt: number): void {
    const w = this.h.world;
    for (const s of this.shoves) {
      const e = w.get(s.id);
      const step = Math.min(dt, s.t);
      s.t -= step;
      if (!e) continue;
      const other = w.get(s.id === this.state!.meId ? this.state!.foeId : this.state!.meId);
      this.h.move(e, s.vx * step, s.vy * step);
      if (other) this.h.face(e, other);
    }
    this.shoves = this.shoves.filter((s) => s.t > 0);
  }
}

/** Who runs a bout between two players on a host: this system once round 2 is on (best of three), else systems/pvpDuel.ts. */
export function pvpBoutHost(w: object): { begin(a: number, b: number): boolean; forfeit(id: number): void; readonly over: boolean } | null {
  return DUEL_FLAGS.v2 ? duelMatchOf(w) : pvpDuelOf(w);
}
